// Libs
import classNames from 'classnames/bind';
import { Box } from '@mui/material';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
// Components, Layouts, Pages
import {
  BaseInputSearch,
  BaseTable,
  BaseTableOption,
  BaseToolbar,
  ConfirmModal,
  Loading,
} from '~/components/common';
import PageShell from '~/pages/common/pageShell/PageShell';
// Others
import useDebounce from '~/utils/hooks/useDebounce';
import { RoleEnum, SearchByFilterContentEnum } from '~/utils/enum/common';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { formatCurrency } from '~/utils/helper/common';
import { IHeaderCellType, IQueryParamBase } from '~/utils/interface/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { IQueryParamContact } from '~/utils/interface/contact';
import { IEstimateTemplate } from '~/utils/interface/estimateTemplate';
import { StorageEnum } from '~/utils/enum/base';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import {
  deleteEstimateTemplate,
  getEstimateTemplateList,
} from '~/thunks/estimateTemplate/estimateTemplateThunk';
import { useAppDispatch } from '~/utils/redux/hooks';
import {
  estimateTemplateActions,
  selectIsRefreshEstimateTemplateList,
} from '~/thunks/estimateTemplate/estimateTemplateSlice';
// Styles, images, icons
import styles from './EstimateTemplate.module.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const columns = (
  t: TFunction,
  onView: (id: string) => void,
  onEdit: (id: string) => void,
  onDelete: (template: IEstimateTemplate) => void
): IHeaderCellType<IEstimateTemplate>[] => {
  return [
    {
      id: 'templateName',
      label: t('table_head_title_name'),
      minWidth: 120,
      render: (record: IEstimateTemplate) => {
        return record.templateName || EMPTY_STRING;
      },
    },
    {
      id: 'total',
      label: t('table_head_title_amount'),
      minWidth: 100,
      render: (record: IEstimateTemplate) => {
        return formatCurrency(record?.total) || EMPTY_STRING;
      },
    },
    {
      id: 'action',
      label: t('table_head_title_action'),
      align: 'left',
      render: (record: IEstimateTemplate) => {
        return (
          <BaseTableOption
            onView={() => onView(record.id)}
            onEdit={() => onEdit(record.id)}
            onDelete={() => onDelete(record)}
          />
        );
      },
    },
  ];
};

const EstimateTemplate = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/estimateTemplate']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(
    () => Number(params?.page ?? BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
    [params?.page]
  );
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  const textSearchParams = useMemo<string>(
    () => String(params?.textSearch || EMPTY_STRING),
    [params?.textSearch]
  );

  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  //#endregion Declare Hook

  //#region Selector
  const isRefreshEstimateTemplateList = useSelector(selectIsRefreshEstimateTemplateList);
  //#endregion Selector

  //#region Declare State
  const [paramObject, setParamObject] = useState<IQueryParamContact>({
    page: pageSelected,
    limit: limitPage,
  });
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [totalItems, setTotalItems] = useState<string>();
  const [estimateTemplateList, setEstimateTemplateList] = useState<IEstimateTemplate[]>([]);
  const [templateSelected, setTemplateSelected] = useState<IEstimateTemplate | null>(null);

  const [isConfirmDeleteTemplate, setIsConfirmDeleteTemplate] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    setSearchKey(textSearchParams);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textSearchParams]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      });

      setSearchParams({
        page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);

  useEffect(() => {
    handleGetEstimateTemplateList(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshEstimateTemplateList) return;

    handleGetEstimateTemplateList(paramObject);
    dispatch(estimateTemplateActions.setRefreshEstimateTemplateList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshEstimateTemplateList]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetEstimateTemplateList = (params: IQueryParamBase) => {
    if (!params) return;

    setIsLoading(true);

    dispatch(getEstimateTemplateList(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses || !pagination || !totalItems) return;

        setEstimateTemplateList(responses);
        setPagination(pagination);
        setTotalItems(String(totalItems));
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEdit = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.updateEstimateTemplate(id));
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.updateEstimateTemplate(id));
        break;
    }
  };

  const handleDelete = (template: IEstimateTemplate) => {
    if (!template) return;

    setTemplateSelected(template);
    setIsConfirmDeleteTemplate(true);
  };

  const handleView = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.updateEstimateTemplate(id));
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.updateEstimateTemplate(id));
        break;
    }
  };

  const handleRowTableClick = (id: string) => {
    handleView(id);
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleNavigateToFormCreate = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.createEstimateTemplate);
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.createEstimateTemplate);
        break;
    }
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleRowPerPageChange = (rowPerPage: number) => {
    setParamObject({ ...paramObject, page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: rowPerPage });
    setSearchParams({
      ...params,
      page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
      limit: String(rowPerPage),
    });
  };

  const handleCloseConfirmDeleteTemplate = () => {
    setIsConfirmDeleteTemplate(false);
    setTemplateSelected(null);
  };

  const handleConfirmDeleteTemplate = () => {
    if (!templateSelected) return;

    setIsLoading(true);

    dispatch(deleteEstimateTemplate(templateSelected?.id))
      .unwrap()
      .then((res) => {
        dispatch(estimateTemplateActions.setRefreshEstimateTemplateList(true));
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
        handleCloseConfirmDeleteTemplate();
      });
  };

  //#endregion Handle Function

  return (
    <PageShell title={t('menu_estimate_template', { ns: 'common/sidebar' })}>
    <Box component={'div'} className={cx('container')} id='EstimateTemplatePage'>
      <BaseToolbar
        title={t('estimate_template_title', { quantity: totalItems })}
        textAction={t('button_add_estimate_template')}
        onAction={handleNavigateToFormCreate}
      >
        <BaseInputSearch
          searchByContent={SearchByFilterContentEnum.NAME}
          value={searchKey}
          onChange={handleSearchChange}
        />
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          currentRowsPerPage={limitPage}
          currentPage={pageSelected}
          count={pagination?.totalPages || DEFAULT_NUMBER_ZERO}
          columns={columns(t, handleView, handleEdit, handleDelete)}
          dataSource={estimateTemplateList || []}
          onPageChange={handlePageChange}
          onRowClick={handleRowTableClick}
          onRowsPerPageChange={handleRowPerPageChange}
        />
      </Box>

      {isConfirmDeleteTemplate && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDeleteTemplate}
          onConfirm={handleConfirmDeleteTemplate}
        />
      )}

      {isLoading && <Loading />}
    </Box>
    </PageShell>
  );
};

export default EstimateTemplate;
